<script>
import Title from "@/components/admin/common/TitleArea.vue"

export default {
  name: "AdminSchemaSnapshotManage",
  meta: {
    title: "어드민-DB스키마스냅샷"
  },
  components: {
    Title,
  },
  data() {
    return {
      description: {
        title: 'DB 스키마 스냅샷'
      },
      snapshots: [],
      isLoading: false,
      creatingTarget: '',
      columns: [
        { name: 'createdAt', label: '생성일시', align: 'left', field: 'createdAt' },
        { name: 'envMode', label: '환경', align: 'center', field: 'envMode' },
        { name: 'target', label: '대상DB', align: 'center', field: 'target' },
        { name: 'database', label: 'DB명', align: 'left', field: 'database' },
        { name: 'actorName', label: '생성자', align: 'left', field: 'actorName' },
        { name: 'size', label: '크기', align: 'right', field: 'size' },
        { name: 'filename', label: '파일명', align: 'left', field: 'filename' },
        { name: 'actions', label: '동작', align: 'center' },
      ]
    }
  },
  methods: {
    formatBytes(size) {
      const num = Number(size || 0)
      if (num < 1024) return `${num} B`
      if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`
      return `${(num / 1024 / 1024).toFixed(1)} MB`
    },
    normalizeSnapshotRow(row = {}) {
      return {
        filename: row.filename || '',
        size: Number(row.size || 0),
        createdAt: row.createdAt || row.generatedAt || new Date().toISOString(),
        target: row.target || null,
        database: row.database || null,
        envMode: row.envMode || null,
        actorMemberId: row.actorMemberId || null,
        actorName: row.actorName || null,
      }
    },
    applySnapshotList(rows = []) {
      const normalized = rows.map((row) => this.normalizeSnapshotRow(row))
      normalized.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      this.snapshots = normalized
    },
    upsertSnapshot(row = {}) {
      const nextRow = this.normalizeSnapshotRow(row)
      const nextRows = [...this.snapshots]
      const targetIndex = nextRows.findIndex((item) => item.filename === nextRow.filename)

      if (targetIndex >= 0) {
        nextRows.splice(targetIndex, 1, nextRow)
      } else {
        nextRows.unshift(nextRow)
      }

      this.applySnapshotList(nextRows)
    },
    async fetchSnapshots() {
      this.isLoading = true
      try {
        const res = await this.$http({
          method: 'GET',
          url: '/api/admin/schema-snapshots',
          name: '[ Axios / Admin / Schema Snapshot List ]'
        })
        this.applySnapshotList(Array.isArray(res.data?.data) ? res.data.data : [])
      } catch (err) {
        this.$log.error(err)
        this.$q.notify({
          type: 'negative',
          message: err?.response?.data?.message || '스키마 스냅샷 목록을 불러오지 못했습니다.'
        })
      } finally {
        this.isLoading = false
      }
    },
    async createSnapshot(target) {
      this.creatingTarget = target
      try {
        const res = await this.$http({
          method: 'POST',
          url: '/api/admin/schema-snapshots',
          data: { target },
          name: '[ Axios / Admin / Create Schema Snapshot ]'
        })
        const warnings = Array.isArray(res.data?.data?.warnings) ? res.data.data.warnings : []
        this.$q.notify({
          type: warnings.length ? 'warning' : 'positive',
          message: warnings.length
            ? `스냅샷은 생성됐지만 일부 메타 조회가 누락되었습니다. (${warnings.length}건)`
            : (res.data?.message || '스키마 스냅샷이 생성되었습니다.')
        })
        this.upsertSnapshot(res.data?.data || {})
        this.fetchSnapshots()
      } catch (err) {
        this.$log.error(err)
        this.$q.notify({
          type: 'negative',
          message: err?.response?.data?.message || '스키마 스냅샷 생성에 실패했습니다.'
        })
      } finally {
        this.creatingTarget = ''
      }
    },
    async downloadSnapshot(filename) {
      try {
        const res = await this.$http({
          method: 'GET',
          url: `/api/admin/schema-snapshots/files/${encodeURIComponent(filename)}`,
          responseType: 'blob',
          name: '[ Axios / Admin / Download Schema Snapshot ]'
        })

        const blob = new Blob([res.data], { type: 'application/json' })
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      } catch (err) {
        this.$log.error(err)
        this.$q.notify({
          type: 'negative',
          message: err?.response?.data?.message || '스키마 스냅샷 다운로드에 실패했습니다.'
        })
      }
    }
  },
  mounted() {
    this.fetchSnapshots()
  }
}
</script>

<template>
  <q-page class="q-pa-lg">
    <div class="row">
      <div class="col">
        <Title :desc="description">
          <q-btn-group>
            <q-btn
              color="primary"
              label="Main DB 스냅샷 생성"
              :loading="creatingTarget === 'main'"
              @click="createSnapshot('main')"
            />
            <q-btn
              color="secondary"
              label="Fulfillment DB 스냅샷 생성"
              :loading="creatingTarget === 'fulfillment'"
              @click="createSnapshot('fulfillment')"
            />
            <q-btn
              color="grey-7"
              text-color="white"
              label="새로고침"
              :loading="isLoading"
              @click="fetchSnapshots"
            />
          </q-btn-group>
        </Title>
      </div>
    </div>

    <div class="row q-mt-lg">
      <div class="col">
        <div class="comp-box">
          <div class="text-subtitle2 q-mb-md">
            현재 환경에 연결된 DB 기준으로 스키마 스냅샷 JSON 파일을 생성합니다.
          </div>

          <q-table
            flat
            bordered
            row-key="filename"
            :rows="snapshots"
            :columns="columns"
            :loading="isLoading"
            :pagination="{ rowsPerPage: 20 }"
          >
            <template v-slot:body-cell-createdAt="props">
              <q-td :props="props">
                {{ $formatDate(props.row.createdAt, "xxxx년 xx월 xx일 hh:mm:ss") }}
              </q-td>
            </template>

            <template v-slot:body-cell-size="props">
              <q-td :props="props">
                {{ formatBytes(props.row.size) }}
              </q-td>
            </template>

            <template v-slot:body-cell-target="props">
              <q-td :props="props">
                <q-badge :color="props.row.target === 'main' ? 'primary' : 'secondary'">
                  {{ props.row.target }}
                </q-badge>
              </q-td>
            </template>

            <template v-slot:body-cell-actions="props">
              <q-td :props="props">
                <q-btn
                  flat
                  round
                  dense
                  icon="download"
                  @click="downloadSnapshot(props.row.filename)"
                />
              </q-td>
            </template>
          </q-table>
        </div>
      </div>
    </div>
  </q-page>
</template>

<style scoped>
</style>
